import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsInstance, db, analytics } from '../../firebase/firebase';
import { logEvent } from 'firebase/analytics';
import {
  collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc,
  setDoc, serverTimestamp, limit, where
} from 'firebase/firestore';
import {
  Loader2, Search as SearchIcon, Zap, DollarSign, Layers, Clock, TrendingUp,
  CheckCircle, Trash2, ChevronDown, ChevronRight, Download,
  Video, X, MousePointerClick, Lock, ArrowDownCircle, XCircle, BarChart2
} from 'lucide-react';
import { Chart } from 'chart.js/auto';
import Select from 'react-select';
import ConfirmationModal from '../ui/ConfirmationModal';
import { SUBJECT_CONFIG } from '../../config/constants';

/* global __app_id */

// Shared tier logic — used by both the live opportunities list and the history
// table's related-keywords section, so badges are calculated consistently everywhere.
function computeOpportunityBadge({ isAiSuggested, competition, demandCount, hasDemandData }) {
  const base = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide shadow-sm border';

  if (isAiSuggested) {
    return { icon: '✨', text: 'AI Suggested', className: `${base} bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white border-purple-300` };
  }
  if (competition == null) return null; // no real data (live or cached) — no badge

  if (!hasDemandData) {
    if (competition < 2000) return { icon: '🟢', text: 'Low Competition', className: `${base} bg-blue-50 text-blue-700 border-blue-200` };
    if (competition < 10000) return { icon: '🟡', text: 'Moderate Competition', className: `${base} bg-slate-50 text-slate-600 border-slate-200` };
    return { icon: '🔴', text: 'High Competition', className: `${base} bg-slate-50 text-slate-500 border-slate-200` };
  }
  if (demandCount > 20 && competition < 2000) {
    return { icon: '💰', text: 'Gold Mine', className: `${base} bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-amber-300` };
  }
  if (demandCount > 50 && competition < 10000) {
    return { icon: '⭐', text: 'Excellent', className: `${base} bg-gradient-to-r from-sky-400 to-blue-500 text-white border-sky-300` };
  }
  if (demandCount > 20 && competition < 10000) {
    return { icon: '👍', text: 'Good', className: `${base} bg-gradient-to-r from-emerald-400 to-teal-500 text-white border-emerald-300` };
  }
  return { icon: '🎯', text: 'Niche', className: `${base} bg-gradient-to-r from-slate-400 to-slate-500 text-white border-slate-300` };
}

// Generate niche categories from the main app config (constants.js)
const NICHE_CATEGORIES = [
  { value: 'Uncategorized', label: 'Uncategorized' },
  ...Object.keys(SUBJECT_CONFIG)
    .sort()
    .map(subject => ({
      value: subject,
      label: subject
    }))
];

/* ========================= Helper UI ========================= */

const MetricBox = ({ icon, label, value, tooltip }) => (
  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 relative group">
    <div className="mx-auto mb-1 w-fit">{icon}</div>
    <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    <div className="absolute top-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <span className="block w-4 h-4 rounded-full bg-slate-300 text-slate-800 text-xs flex items-center justify-center cursor-help" title={tooltip}>?</span>
    </div>
  </div>
);

/* ========================= Trend Modal ========================= */
const TrendModal = ({ keyword, history, isLoading, onClose }) => {
  const popularityChartRef = useRef(null);
  const competitionChartRef = useRef(null);
  const popularityChartInstance = useRef(null);
  const competitionChartInstance = useRef(null);
  const [activeChart, setActiveChart] = useState('popularity');

  const hasData = Array.isArray(history) && history.length >= 1;

  const chartData = useMemo(() => {
    if (!hasData) return { labels: [], popularity: [], competition: [] };
    return {
      labels: history.map(h => h.month),
      popularity: history.map(h => h.popularity ?? null),
      competition: history.map(h => h.totalProducts ?? null),
    };
  }, [history, hasData]);

  useEffect(() => {
    if (!hasData) return;
    const configs = {
      popularity: { ref: popularityChartRef, instance: popularityChartInstance, data: chartData.popularity, label: 'Popularity', color: '#0ea5e9', fill: 'rgba(14,165,233,0.1)' },
      competition: { ref: competitionChartRef, instance: competitionChartInstance, data: chartData.competition, label: 'Competition', color: '#8b5cf6', fill: 'rgba(139,92,246,0.1)' },
    };
    const cfg = configs[activeChart];
    if (!cfg?.ref.current) return;
    if (cfg.instance.current) cfg.instance.current.destroy();
    try {
      cfg.instance.current = new Chart(cfg.ref.current.getContext('2d'), {
        type: 'line',
        data: { labels: chartData.labels, datasets: [{ label: cfg.label, data: cfg.data, borderColor: cfg.color, backgroundColor: cfg.fill, fill: true, tension: 0.3, pointRadius: 4, pointHoverRadius: 6, pointBackgroundColor: cfg.color }] },
        options: { responsive: true, maintainAspectRatio: false, interaction: { mode: 'nearest', intersect: false }, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: false, grid: { color: '#e2e8f0' }, ticks: { color: '#64748b' } }, x: { grid: { display: false }, ticks: { color: '#64748b' } } } }
      });
    } catch { /* swallow */ }
    return () => { if (cfg.instance.current) { cfg.instance.current.destroy(); cfg.instance.current = null; } };
  }, [hasData, activeChart, chartData]);

  useEffect(() => {
    return () => {
      if (popularityChartInstance.current) popularityChartInstance.current.destroy();
      if (competitionChartInstance.current) competitionChartInstance.current.destroy();
    };
  }, []);

  const trendCell = (diff) => {
    if (diff === null) return <span className="text-slate-300">—</span>;
    if (diff > 0) return <span className="text-green-600 font-semibold">▲ +{diff.toLocaleString()}</span>;
    if (diff < 0) return <span className="text-red-500 font-semibold">▼ {diff.toLocaleString()}</span>;
    return <span className="text-slate-400 font-medium">→ 0</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] rounded-xl bg-white border border-slate-300 shadow-2xl flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 border-b border-slate-200 flex-shrink-0">
          <div>
            <h3 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <TrendingUp className="text-sky-600" size={24} />
              Monthly Trend Analysis
              <span className="bg-amber-100 text-amber-700 text-xs px-2 py-1 rounded-full font-bold border border-amber-300">BETA</span>
            </h3>
            <p className="text-sm text-slate-500 mt-1">Keyword: <span className="font-semibold text-sky-600">"{keyword}"</span></p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 rounded-full hover:bg-slate-100"><X size={24} /></button>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center"><Loader2 className="animate-spin text-sky-500 mx-auto mb-3" size={32} /><p className="text-slate-600">Loading trend history...</p></div>
            </div>
          ) : !hasData ? (
            <div className="bg-slate-50 rounded-xl p-8 text-center border border-slate-200">
              <TrendingUp className="mx-auto mb-4 text-slate-300" size={48} />
              <p className="text-lg font-medium text-slate-700 mb-2">Not enough history yet</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Trend data builds up over time as this keyword is searched and its cache refreshes each month.</p>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700 font-medium">💡 Trend data will accumulate as more users search this keyword over time.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {history.length >= 2 && (
                <>
                  <div className="flex gap-2">
                    {[{ key: 'popularity', label: 'Popularity', activeClass: 'bg-sky-600 border-sky-600 text-white' }, { key: 'competition', label: 'Competition', activeClass: 'bg-purple-600 border-purple-600 text-white' }].map(tab => (
                      <button key={tab.key} onClick={() => setActiveChart(tab.key)} className={`text-xs px-3 py-1.5 rounded-md border font-medium transition-colors ${activeChart === tab.key ? tab.activeClass : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>{tab.label}</button>
                    ))}
                  </div>
                  <div className="h-52 bg-slate-50 rounded-lg border border-slate-200 p-3">
                    {activeChart === 'popularity' && <canvas ref={popularityChartRef} />}
                    {activeChart === 'competition' && <canvas ref={competitionChartRef} />}
                  </div>
                </>
              )}

              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="font-medium text-slate-700 mb-1">📊 How to read this data:</p>
                <span className="mr-4"><strong>Popularity:</strong> How often teachers search this keyword</span>
                <span className="mr-4"><strong>Competition:</strong> Total products competing</span>
                <span><strong>✦</strong> = most recent snapshot</span>
                {history.length === 1 && <span className="ml-4 text-amber-600 font-medium">· Only 1 snapshot so far — more will build over time</span>}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border border-slate-200 rounded-lg">
                  <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
                    <tr>
                      <th className="p-3 font-semibold">Month</th>
                      <th className="p-3 text-center font-semibold">Popularity</th>
                      {history.length >= 2 && <th className="p-3 text-center font-semibold">Change</th>}
                      <th className="p-3 text-center font-semibold">Competition</th>
                      {history.length >= 2 && <th className="p-3 text-center font-semibold">Change</th>}
                      <th className="p-3 text-center font-semibold">Avg 1st Page Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {history.map((row, idx) => {
                      const prev = idx > 0 ? history[idx - 1] : null;
                      const popDiff = (prev && row.popularity != null && prev.popularity != null) ? row.popularity - prev.popularity : null;
                      const compDiff = (prev && row.totalProducts != null && prev.totalProducts != null) ? row.totalProducts - prev.totalProducts : null;
                      const isLatest = idx === history.length - 1;
                      return (
                        <tr key={row.month} className={`hover:bg-slate-50 ${isLatest ? 'bg-sky-50/60 font-semibold' : ''}`}>
                          <td className="p-3 text-slate-800">{row.month}{isLatest ? ' ✦' : ''}</td>
                          <td className="p-3 text-center text-sky-700 font-semibold">{row.popularity != null ? row.popularity.toLocaleString() : '—'}</td>
                          {history.length >= 2 && <td className="p-3 text-center">{trendCell(popDiff)}</td>}
                          <td className="p-3 text-center text-slate-700 font-semibold">{row.totalProducts != null ? row.totalProducts.toLocaleString() : '—'}</td>
                          {history.length >= 2 && <td className="p-3 text-center">{trendCell(compDiff)}</td>}
                          <td className="p-3 text-center text-green-700 font-semibold">{row.firstPageAvgPrice != null ? `$${Number(row.firstPageAvgPrice).toFixed(2)}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 italic text-center">One snapshot recorded per month when this keyword's cache refreshes (every 7 days)</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const OpportunitiesList = ({ opportunities = [], onUseKeyword }) => {
  // Per-keyword state for on-demand competition checks: { [keyword]: { status: 'idle'|'loading'|'done'|'error', competition, searchCount } }
  const [competitionChecks, setCompetitionChecks] = useState({});
  const autoCheckedRef = useRef(new Set()); // prevents re-firing for keywords already requested

  const runCompetitionCheck = useCallback(async (keyword) => {
    setCompetitionChecks(prev => ({ ...prev, [keyword]: { status: 'loading' } }));
    try {
      const fn = httpsCallable(functionsInstance, 'fetchOpportunityCompetition');
      const result = await fn({ keyword });
      const { competition, searchCount } = result.data || {};
      setCompetitionChecks(prev => ({
        ...prev,
        [keyword]: { status: 'done', competition: competition ?? null, searchCount: searchCount ?? 0 }
      }));
    } catch (err) {
      console.error('fetchOpportunityCompetition failed:', err);
      setCompetitionChecks(prev => ({ ...prev, [keyword]: { status: 'error' } }));
    }
  }, []);

  const handleCheckCompetition = useCallback((e, keyword) => {
    e.stopPropagation(); // don't trigger the row's onUseKeyword click
    runCompetitionCheck(keyword);
  }, [runCompetitionCheck]);

  // Auto-check the top 10 suggestions that have NO cached data at all — these are the
  // only ones with nothing to show otherwise. Anything already cached (within the last
  // 7 days, per the server's cache check) is shown immediately and never auto-refetched;
  // the user can hit "Refresh" manually if they want a live re-check.
  useEffect(() => {
    const topKeywords = opportunities
      .filter(o => o?.source !== 'ai_suggested' && o?.keyword && o.cachedCompetition == null && o.cachedPopularity == null)
      .slice(0, 10)
      .map(o => o.keyword);

    topKeywords.forEach(keyword => {
      if (autoCheckedRef.current.has(keyword)) return;
      autoCheckedRef.current.add(keyword);
      runCompetitionCheck(keyword);
    });
  }, [opportunities, runCompetitionCheck]);

  return (
    <div className="flex flex-col">
      <h3 className="text-xl font-semibold text-sky-700 flex items-center gap-2">
        Keyword Opportunities
      </h3>
      <p className="text-sm text-slate-500 mb-4">
        These are the suggested words that appear in the TPT search bar when typing your selected keyword. Keywords we already have data on show it right away — anything brand new gets checked automatically. Click the keyword itself to analyze it directly.
      </p>

      <div className="space-y-2 overflow-y-auto pr-2 flex-grow h-96 lg:h-auto lg:max-h-[28rem]">
        {opportunities.length === 0 ? (
          <p className="text-center text-sm text-slate-400 pt-8">No keyword suggestions were found for this search.</p>
        ) : (
          opportunities.map(item => {
            const isAiSuggested = item?.source === 'ai_suggested';
            const check = competitionChecks[item.keyword] || { status: 'idle' };

            // Historical data we already have cached for this exact keyword, if any
            const hasCachedData = item?.cachedCompetition != null || item?.cachedPopularity != null;
            const cachedDateLabel = item?.cachedAt
              ? new Date(item.cachedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
              : null;

            // Competition: prefer a fresh live check if one has been run; otherwise show cached
            const comp = check.status === 'done' ? check.competition : (hasCachedData ? item.cachedCompetition : null);

            // Demand: only real historical popularity (the TPT-scale number the Gold Mine/Excellent/
            // Good thresholds were calibrated against) counts as a real demand signal. Our own
            // searchCount is too new/sparse a metric to reliably grade against those same thresholds —
            // using it here would mislabel plenty of genuinely low-competition keywords as "Niche"
            // just because they haven't accumulated internal search history yet.
            const demandCount = item?.cachedPopularity ?? null;
            const hasDemandData = demandCount != null && demandCount > 0;

            const badge = computeOpportunityBadge({
              isAiSuggested,
              competition: comp,
              demandCount,
              hasDemandData,
            });

            return (
              <div
                key={item.keyword}
                onClick={() => onUseKeyword?.(item.keyword)}
                className="bg-slate-50/80 p-3 rounded-lg hover:bg-sky-50 border border-slate-200 hover:border-sky-300 cursor-pointer transition-all"
              >
                <div className="flex justify-between items-start">
                  <p className="font-semibold text-slate-800 text-base">{item.keyword}</p>
                  {badge && (
                    <span className={badge.className}>{badge.icon} {badge.text}</span>
                  )}
                </div>
                <div className="flex items-center mt-2 text-sm flex-wrap gap-y-1">
                  {isAiSuggested ? (
                    <span className="text-xs text-slate-400 italic">Click to search &amp; see live data</span>
                  ) : (
                    <>
                      {/* Popularity: always shows our last recorded historical value, independent of any live competition check */}
                      <span className="font-medium text-slate-500 flex items-center gap-1">
                        {item.cachedPopularity != null ? (
                          <>
                            <span className="text-amber-500" title={cachedDateLabel ? `Cached from ${cachedDateLabel}` : 'From a previous search'}>★</span>
                            Popularity: <span className="font-bold text-sky-600">{item.cachedPopularity.toLocaleString()}</span>
                          </>
                        ) : (
                          <>Popularity: <span className="font-bold text-slate-400">—</span></>
                        )}
                      </span>
                      <span className="mx-3 text-slate-300">|</span>

                      {/* Competition: live check result if run, otherwise cached, otherwise loading/error/button */}
                      {check.status === 'loading' ? (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Checking...
                        </span>
                      ) : check.status === 'error' ? (
                        <button
                          onClick={(e) => handleCheckCompetition(e, item.keyword)}
                          className="text-xs font-semibold text-red-500 hover:underline"
                        >
                          Failed — retry
                        </button>
                      ) : comp != null ? (
                        <span className="font-medium text-slate-500">
                          Competition: <span className="font-bold text-red-600">{comp.toLocaleString()}</span>
                          <button
                            onClick={(e) => handleCheckCompetition(e, item.keyword)}
                            className="ml-2 text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline"
                          >
                            Refresh
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={(e) => handleCheckCompetition(e, item.keyword)}
                          className="text-xs font-semibold text-sky-600 hover:text-sky-800 hover:underline"
                        >
                          Check competition
                        </button>
                      )}

                      {cachedDateLabel && check.status !== 'done' && (
                        <span className="w-full text-[11px] text-slate-400 italic mt-0.5">
                          Cached from {cachedDateLabel}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

/* ====== Top 5 Ranking Products ====== */
const TopProductsList = ({ products = [] }) => {
  const list = products?.slice(0, 5) || [];

  return (
    <div className="flex flex-col">
      <h3 className="text-xl font-semibold text-sky-700">Top 5 Ranking Products</h3>
      <p className="text-sm text-slate-500 mb-4">A live look at the top-ranking products for this keyword right now.</p>

      <div className="space-y-3 overflow-y-auto pr-2 flex-grow h-96 lg:h-auto lg:max-h-[28rem]">
        {list.length === 0 ? (
          <p className="text-center text-sm text-slate-400 pt-8">No products found.</p>
        ) : (
          list.map((comp, idx) => {
            const priceNum = typeof comp?.price === 'number'
              ? comp.price
              : Number(comp?.price?.toString().replace(/[^0-9.]/g, '')) || 0;

            return (
              <div key={`${comp?.rank ?? idx}-${comp?.url ?? idx}`} className="p-3 rounded-lg bg-slate-50/70 border border-slate-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-sky-700">Rank #{comp?.rank ?? (idx + 1)}</p>
                    <a
                      href={comp?.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-slate-700 hover:text-sky-600 line-clamp-2 leading-tight"
                    >
                      {comp?.title || 'Untitled Product'}
                    </a>
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    <span className="font-bold text-green-700">
                      {priceNum > 0 ? `$${priceNum.toFixed(2)}` : 'Free'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

/* ========================= Main ========================= */

const KeywordResearchTool = ({ currentUser, triggerAuthModal, userAccessData }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [activeTab, setActiveTab] = useState('search'); // 'search' | 'history' | 'opportunities'
  const [tabAnalysisResult, setTabAnalysisResult] = useState({ search: null, history: null, opportunities: null });
  const [userHistory, setUserHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [keywordToDelete, setKeywordToDelete] = useState(null);

  // --- PAGINATION & SEARCH STATE ---
  // UPDATED: Default limit set to 15 to reduce reads
  const [historyLimit, setHistoryLimit] = useState(15);
  const [historySearchInput, setHistorySearchInput] = useState(''); // Text in input
  const [historyActiveSearch, setHistoryActiveSearch] = useState(''); // Actually applied search

  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showInteractiveModal, setShowInteractiveModal] = useState(false);

  const [sortBy, setSortBy] = useState({ key: 'lastSearchedAt', dir: 'desc' });
  const [nicheFilter, setNicheFilter] = useState('All');
  const [expanded, setExpanded] = useState(() => new Set());
  const [relatedCompetitionChecks, setRelatedCompetitionChecks] = useState({}); // { [keyword]: { status, competition, searchCount } }

  const runRelatedCompetitionCheck = useCallback(async (keyword) => {
    setRelatedCompetitionChecks(prev => ({ ...prev, [keyword]: { status: 'loading' } }));
    try {
      const fn = httpsCallable(functionsInstance, 'fetchOpportunityCompetition');
      const result = await fn({ keyword });
      const { competition, searchCount } = result.data || {};
      setRelatedCompetitionChecks(prev => ({
        ...prev,
        [keyword]: { status: 'done', competition: competition ?? null, searchCount: searchCount ?? 0 }
      }));
    } catch (err) {
      console.error('fetchOpportunityCompetition (history) failed:', err);
      setRelatedCompetitionChecks(prev => ({ ...prev, [keyword]: { status: 'error' } }));
    }
  }, []);

  const handleCheckRelatedCompetition = useCallback((keyword) => {
    runRelatedCompetitionCheck(keyword);
  }, [runRelatedCompetitionCheck]);

  // No auto-checking here on purpose — the history table only ever shows whatever
  // is already stored for each related keyword (regardless of age). A live check
  // only runs when the user explicitly clicks "Check" or "Refresh" on a row.

  // --- TRIAL LOGIC ---
  const TRIAL_LIMIT = 5;
  const isTrial = userAccessData?.accessStatus === 'trial' && !userAccessData?.approvedByAdmin;
  const usageCount = userAccessData?.trialKeywordSearchesUsed || 0;
  const isLimitReached = isTrial && usageCount >= TRIAL_LIMIT;

  // --- TRENDING/OPPORTUNITIES STATE ---
  const [opportunityData, setOpportunityData] = useState({
    topOpportunities: [],
    mostPopular: [],
    lowCompetition: []
  });
  const [activeOpportunityView, setActiveOpportunityView] = useState('topOpportunities');
  const [isTrendingLoading, setIsTrendingLoading] = useState(true);
  const [trendingRefreshedAt, setTrendingRefreshedAt] = useState(null);

  // Derived state for current view
  const currentOpportunities = opportunityData[activeOpportunityView] || [];
  const trendingKeywords = currentOpportunities; // Backward compatibility

  // --- POPULARITY HISTORY STATE (per-keyword trend chart) ---
  const [popularityHistory, setPopularityHistory] = useState(null);
  const [isHistoryChartLoading, setIsHistoryChartLoading] = useState(false);
  const [activeHistoryKeyword, setActiveHistoryKeyword] = useState(null);
  const [showTrendModal, setShowTrendModal] = useState(false);

  // Fetch trending on mount
  useEffect(() => {
    if (!currentUser) { setIsTrendingLoading(false); return; }
    const fetchTrending = httpsCallable(functionsInstance, 'fetchKeywordAnalysis');
    fetchTrending({ getTrendingData: true })
      .then(res => {
        const data = res.data || {};
        setOpportunityData({
          topOpportunities: data.trending || [],
          mostPopular: data.mostPopular || [],
          lowCompetition: data.lowCompetition || []
        });
        const ra = data.refreshedAt;
        // Handle Firestore Timestamp (has .seconds) or plain object from serialization
        const seconds = ra?._seconds ?? ra?.seconds ?? null;
        setTrendingRefreshedAt(seconds ? { seconds } : null);
      })
      .catch(err => console.warn('Failed to load trending keywords:', err?.message))
      .finally(() => setIsTrendingLoading(false));
  }, [currentUser]);

  // CSV Download function
  const downloadOpportunitiesCSV = () => {
    const data = currentOpportunities;
    if (!data.length) return;

    const viewNames = {
      topOpportunities: 'Top_Opportunities',
      mostPopular: 'Most_Popular_Keywords',
      lowCompetition: 'Low_Competition_Keywords'
    };

    const filename = `${viewNames[activeOpportunityView]}_${new Date().toISOString().split('T')[0]}.csv`;

    const csvHeader = ['Rank', 'Keyword', 'Golden Score', 'Popularity', 'Competition', 'Search Count'];
    const csvRows = data.map((item, idx) => [
      idx + 1,
      `"${item.keyword}"`,
      item.goldenScore || '',
      item.popularity || '',
      item.totalProducts || '',
      item.searchCount || ''
    ]);

    const csvContent = [csvHeader, ...csvRows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch popularity history for a keyword and show in modal
  const fetchPopularityHistory = useCallback(async (keyword) => {
    if (!currentUser || !keyword) return;
    setIsHistoryChartLoading(true);
    setActiveHistoryKeyword(keyword);
    setPopularityHistory(null);
    setShowTrendModal(true);
    try {
      const fn = httpsCallable(functionsInstance, 'fetchKeywordAnalysis');
      const res = await fn({
        keyword,
        getPopularityHistory: true  // Use the same working function with a different flag
      });
      setPopularityHistory(res.data?.history || []);
    } catch (err) {
      console.warn('Failed to load popularity history:', err?.message);
      setPopularityHistory([]);
    } finally {
      setIsHistoryChartLoading(false);
    }
  }, [currentUser]);

  const toggleExpand = (rowId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const getPrivateUserCollectionRef = useCallback((collectionName) => {
    if (!currentUser) return null;
    const defaultAppId = 'mrjoel-tpt-app';
    const appId =
      (typeof window !== 'undefined' && typeof window.__app_id !== 'undefined' && window.__app_id) ? window.__app_id
      : (typeof __app_id !== 'undefined' && __app_id) ? __app_id
      : defaultAppId;
    return collection(db, 'artifacts', appId, 'users', currentUser.uid, collectionName);
  }, [currentUser]);

  const handleSearch = useCallback(async (keyword, source = 'main_search_bar') => {
    if (!currentUser) {
      triggerAuthModal('Please log in to perform keyword research.');
      return;
    }

    if (isLimitReached) {
      setError('You have reached your trial limit for keyword searches. Please upgrade to continue.');
      return;
    }

    const cleanKeyword = keyword?.trim();
    if (!cleanKeyword) {
      setError('Please enter a keyword to search.');
      return;
    }

    setIsSearching(true);
    setError('');
    setAnalysisResult(null);

    // Determine which tab should show this result
    // history/opportunity sources stay in their tab, main search bar uses search tab
    const targetTab = (source === 'history_analyze_button' || source === 'history_keyword_link' || source === 'history_related_analyze_button' || source === 'history_related_keyword_link')
      ? 'history'
      : (source === 'trending_analyze' || source === 'trending_panel_click')
      ? 'opportunities'
      : 'search';

    // Switch to the right tab and clear its previous result
    setActiveTab(targetTab);
    setTabAnalysisResult(prev => ({ ...prev, [targetTab]: null }));

    try {
      if (analytics) {
        logEvent(analytics, 'keyword_search', {
          keyword: cleanKeyword,
          source: source
        });
      }

      const fetchAnalysis = httpsCallable(functionsInstance, 'fetchKeywordAnalysis');
      const result = await fetchAnalysis({ keyword: cleanKeyword });
      const data = result.data;

      if (data?.keyword) {
        const computedPopularityFromHits =
          (data.opportunities || []).find(
            o => (o.keyword || '').toLowerCase() === (data.keyword || '').toLowerCase()
          )?.popularity ?? null;

        const searchedPopularity = (typeof data.popularity === 'number')
          ? data.popularity
          : (typeof computedPopularityFromHits === 'number' ? computedPopularityFromHits : null);

        const enriched = { ...data, popularity: searchedPopularity };
        setAnalysisResult(enriched);
        setTabAnalysisResult(prev => ({ ...prev, [targetTab]: enriched }));

        // Note: history is written server-side by fetchKeywordAnalysis itself (with
        // cachedPopularity/cachedCompetition enrichment and null-safe competition
        // handling). A client-side write used to happen here too, but it rebuilt the
        // related array from only the plain popularity/competition fields and
        // overwrote the server's correctly-enriched data on every single search —
        // removed for good, not just for this fix.
      } else {
        setError('Search returned no data. Check the keyword and try again.');
      }
    } catch (err) {
      console.error('Error calling fetchKeywordAnalysis:', err);
      if (err.code === 'permission-denied') {
         setError(err.message);
      } else {
         const message = err?.message?.includes('INTERNAL')
        ? 'The TPT scraping service failed. Please try again later.'
        : (err?.message || 'Unexpected error.');
         setError(message);
      }
    } finally {
      setIsSearching(false);
    }
  }, [currentUser, triggerAuthModal, getPrivateUserCollectionRef, isLimitReached]);

  // --- HISTORY LISTENER — lazy loaded, only starts when history tab first opened ---
  useEffect(() => {
    if (activeTab !== 'history') return; // Don't load until tab is clicked
    if (!currentUser) {
      setUserHistory([]);
      setIsHistoryLoading(false);
      return;
    }
    setIsHistoryLoading(true);
    setHistoryLoaded(true);

    const historyColRef = getPrivateUserCollectionRef('keywordResearchHistory');
    if (!historyColRef) {
      setIsHistoryLoading(false);
      setError('Configuration error: User ID missing.');
      return;
    }

    let qy;

    if (historyActiveSearch) {
        // --- SEARCH MODE (Server-side to save reads on non-matching docs) ---
        const term = historyActiveSearch.toLowerCase();
        const endTerm = term + '\uf8ff';

        qy = query(
            historyColRef,
            where('keyword', '>=', term),
            where('keyword', '<=', endTerm),
            orderBy('keyword'),
            limit(historyLimit)
        );
    } else {
        // --- BROWSE MODE (Most Recent) ---
        // Limits reads to 'historyLimit' (initially 15)
        qy = query(
            historyColRef,
            orderBy('lastSearchedAt', 'desc'),
            limit(historyLimit)
        );
    }

    const unsubscribe = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setUserHistory(rows);
        setIsHistoryLoading(false);
      },
      (err) => {
        console.error('Error fetching keyword history:', err);
        if(err.message.includes('index')) {
            console.warn("Missing index for history search. Create one via Firebase Console.");
        }
        setError('Failed to load keyword history.');
        setIsHistoryLoading(false);
      }
    );
    return () => unsubscribe();
  }, [activeTab, currentUser, getPrivateUserCollectionRef, historyLimit, historyActiveSearch]);

  const handleHistorySearch = useCallback(() => {
      setHistoryActiveSearch(historySearchInput.trim());
      setHistoryLimit(15);
  }, [historySearchInput]);

  const clearHistorySearch = () => {
      setHistorySearchInput('');
      setHistoryActiveSearch('');
      setHistoryLimit(15);
  };

  const handleUpdateNiche = useCallback(async (keywordId, newNiche) => {
    if (!currentUser || !keywordId) return;
    try {
      if (analytics) {
        logEvent(analytics, 'keyword_update_niche', {
          keyword_id: keywordId,
          new_niche: newNiche
        });
      }

      const historyColRef = getPrivateUserCollectionRef('keywordResearchHistory');
      if (!historyColRef) return;
      const docRef = doc(historyColRef, keywordId);
      await updateDoc(docRef, { niche: newNiche, lastUpdated: new Date() });
    } catch (e) {
      console.error('Failed to update niche:', e);
      setError('Failed to save niche category.');
    }
  }, [currentUser, getPrivateUserCollectionRef]);

  const openDeleteConfirm = (keywordId) => {
    setKeywordToDelete(keywordId);
    setShowConfirmModal(true);
  };

  const handleDeleteKeyword = useCallback(async () => {
    if (!currentUser || !keywordToDelete) return;
    try {
      if (analytics) {
        logEvent(analytics, 'keyword_delete', {
          keyword_id: keywordToDelete
        });
      }

      const historyColRef = getPrivateUserCollectionRef('keywordResearchHistory');
      if (!historyColRef) return;
      const docRef = doc(historyColRef, keywordToDelete);
      await deleteDoc(docRef);
    } catch (err) {
      console.error('Error deleting keyword history:', err);
      setError('Failed to delete keyword history.');
    } finally {
      setKeywordToDelete(null);
      setShowConfirmModal(false);
    }
  }, [currentUser, getPrivateUserCollectionRef, keywordToDelete]);

  const formatLastSearched = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleExportCSV = useCallback(() => {
    if (analytics) {
      logEvent(analytics, 'keyword_export_csv', {
        row_count: userHistory.length
      });
    }

    const headers = [
      'type', 'parent_keyword', 'keyword', 'popularity', 'competition', 'avg_price', 'niche', 'last_searched'
    ];

    const rows = [];

    userHistory.forEach(row => {
      const searchedDate = row.lastSearchedAt?.toDate
        ? row.lastSearchedAt.toDate()
        : (row?.lastSearchedAt?.seconds ? new Date(row.lastSearchedAt.seconds * 1000) : null);

      rows.push([
        'searched',
        '',
        safeCSV(row.keyword),
        toNum(row.popularity),
        toNum(row.competition ?? row.totalProducts),
        toNum(row.firstPageAvgPrice),
        safeCSV(row.niche || ''),
        searchedDate ? searchedDate.toISOString() : ''
      ]);

      if (Array.isArray(row.related)) {
        row.related.forEach(rel => {
          rows.push([
            'related',
            safeCSV(row.keyword),
            safeCSV(rel.keyword),
            toNum(rel.popularity),
            toNum(rel.competition),
            '', '', searchedDate ? searchedDate.toISOString() : ''
          ]);
        });
      }
    });

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStr = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
    a.href = url;
    a.download = `tpt_keyword_research_${dateStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    function toNum(v) {
      if (v === null || v === undefined) return '';
      const n = Number(v);
      return Number.isFinite(n) ? n : '';
    }
    function safeCSV(v) {
      if (v === null || v === undefined) return '';
      const s = String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    }
  }, [userHistory]);

  const toggleSort = (key) => {
    setSortBy(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const sortedHistory = useMemo(() => {
    const filtered = userHistory.filter(item =>
      nicheFilter === 'All' || item.niche === nicheFilter
    );
    const arr = [...filtered];
    const { key, dir } = sortBy;
    const mult = dir === 'asc' ? 1 : -1;

    const valueForKey = (s, k) => {
        if (k === 'lastSearchedAt' && s.lastSearchedAt?.seconds) return s.lastSearchedAt.seconds;
        if (k === 'keyword' || k === 'niche') return (s[k] || '').toLowerCase();
        if (k === 'competition') return Number(s.competition ?? s.totalProducts ?? 0);
        if (k === 'popularity') return Number(s.popularity ?? 0);
        if (k === 'firstPageAvgPrice') return Number(s.firstPageAvgPrice ?? 0);
        return s[k] ?? 0;
    };

    return arr.sort((a, b) => {
      const A = valueForKey(a, key);
      const B = valueForKey(b, key);
      if (A === B) return 0;
      if (typeof A === 'string') return A.localeCompare(B) * mult;
      return (A > B ? 1 : -1) * mult;
    });
  }, [userHistory, sortBy, nicheFilter]);

  const SearchResultDisplay = useMemo(() => {
    const result = tabAnalysisResult[activeTab] || analysisResult;
    if (!result) return null;
    const { keyword, totalProducts, firstPageAvgPrice, opportunities, firstPageProducts, cachedAt, popularity } = result;
    const isCached = cachedAt && typeof cachedAt.seconds === 'number';
    const firstPageAvgPriceDisplay =
      firstPageAvgPrice > 0 ? `$${firstPageAvgPrice.toFixed(2)}` : 'Free/N/A';

    const popularityDisplay = (typeof popularity === 'number') ? popularity.toLocaleString() : '—';

    return (
      <div className="bg-white p-6 rounded-2xl shadow-xl border border-sky-200 animate-fade-in space-y-6">
        <div className="flex justify-between items-start border-b pb-4 border-slate-200">
          <h3 className="text-xl font-semibold text-sky-700 flex items-center gap-2">
            <Layers className="w-6 h-6 text-sky-600" /> {`"${keyword}" Analysis`}
          </h3>
          {isCached && (
            <span className="text-xs text-green-600 bg-green-100 px-3 py-1 rounded-full font-semibold flex items-center gap-1">
              <CheckCircle size={14} /> Global Cache Hit ({formatLastSearched(cachedAt)})
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <MetricBox
            icon={<SearchIcon size={24} className="text-sky-600" />}
            label="Total Competition (Products)"
            value={Number(totalProducts || 0).toLocaleString()}
            tooltip="The approximate total number of products found for this keyword."
          />
          <MetricBox
            icon={<DollarSign size={24} className="text-green-600" />}
            label="1st Page Avg Price"
            value={firstPageAvgPriceDisplay}
            tooltip="The average price of products on the first page of search results."
          />
          <MetricBox
            icon={<TrendingUp size={24} className="text-indigo-600" />}
            label="Top Opportunities"
            value={(opportunities || []).length}
            tooltip="Number of related keywords with Algolia search data."
          />
          <MetricBox
            icon={<Zap size={24} className="text-amber-600" />}
            label="Popularity (Searched Term)"
            value={popularityDisplay}
            tooltip="Algolia 'popularity' score for the exact searched phrase (if available)."
          />
          <MetricBox
            icon={<Clock size={24} className="text-slate-600" />}
            label="Cache Validity"
            value="7 Days"
            tooltip="This data is globally cached and valid for 7 days."
          />
        </div>

        {/* Trend History & Quick Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="text-sm text-slate-500">
            Want to see how this keyword's popularity and competition have changed over time?
          </div>
          <button
            onClick={() => fetchPopularityHistory(keyword)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-300 transition-colors relative"
          >
            <TrendingUp size={16} />
            View Trend History
            <span className="absolute -top-1 -right-1 bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full font-bold border border-amber-300">
              BETA
            </span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4 border-t border-slate-100">
          <OpportunitiesList
            opportunities={opportunities || []}
            onUseKeyword={(keyword) => handleSearch(keyword, 'analysis_opportunity_click')}
          />
          <TopProductsList products={firstPageProducts || []} />
        </div>
      </div>
    );
  }, [analysisResult, tabAnalysisResult, activeTab, handleSearch, fetchPopularityHistory]);

  const sortIcon = useCallback((key) => sortBy.key === key ? (sortBy.dir === 'asc' ? '↑' : '↓') : '', [sortBy]);

  const HistoryTable = useMemo(() => {
    if (!currentUser) return (
      <p className="text-center p-4 text-sm text-slate-500 mt-4">Log in to view and save your keyword research history.</p>
    );
    if (isHistoryLoading) return (
      <div className="flex justify-center p-8"><Loader2 className="animate-spin text-sky-500" /></div>
    );
    if (!historyLoaded) return (
      <div className="text-center p-4 text-sm text-slate-400">Click this tab to load your history.</div>
    );
    if (userHistory.length === 0 && !historyActiveSearch) return (
      <div className="text-center p-4 text-sm text-slate-500 mt-4">Your keyword history is empty. Start a search above!</div>
    );

    return (
      <>
        <div className="flex flex-wrap items-center justify-between mb-3 gap-4">

          {/* --- NEW HISTORY SEARCH BAR --- */}
          <div className="flex gap-2 flex-grow max-w-md">
            <div className="relative flex-grow">
                <input
                    type="text"
                    placeholder="Search saved keywords..."
                    className="w-full pl-9 pr-8 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 outline-none"
                    value={historySearchInput}
                    onChange={(e) => setHistorySearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleHistorySearch()}
                />
                <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                {historySearchInput && (
                    <button onClick={clearHistorySearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <XCircle size={16} />
                    </button>
                )}
            </div>
            <button
                onClick={handleHistorySearch}
                className="px-3 py-1.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-medium border border-slate-300"
            >
                Search
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Filter by Niche:</span>
            <Select
              options={[{ value: 'All', label: 'All Niches' }, ...NICHE_CATEGORIES]}
              value={NICHE_CATEGORIES.find(c => c.value === nicheFilter) || { value: nicheFilter, label: nicheFilter === 'All' ? 'All Niches' : nicheFilter }}
              onChange={(selected) => setNicheFilter(selected.value)}
              styles={{
                control: (p) => ({ ...p, minHeight: 30, height: 30, fontSize: '0.85rem' }),
                indicatorsContainer: (p) => ({ ...p, height: 30 }),
                valueContainer: (p) => ({ ...p, padding: '0 8px' }),
                singleValue: (p) => ({ ...p, fontSize: '0.85rem' }),
                option: (p) => ({ ...p, fontSize: '0.85rem' }),
                container: (p) => ({ ...p, width: '200px' })
              }}
            />

            <button
              onClick={handleExportCSV}
              className="ml-2 inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-lg bg-sky-600 hover:bg-sky-700 text-white"
              title="Download CSV of keywords & related opportunities"
            >
              <Download size={14} /> CSV
            </button>
          </div>
        </div>

        {/* HELPER TEXT FOR PAGINATION LIMIT */}
        {!historyActiveSearch && (
          <p className="text-xs text-slate-400 mb-2 italic">
            Showing the {Math.min(userHistory.length, historyLimit)} most recent searches...
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="p-3 w-8"></th>
                <th className="p-3 font-medium cursor-pointer" onClick={() => toggleSort('keyword')}>Keyword {sortIcon('keyword')}</th>
                <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('popularity')}>Popularity {sortIcon('popularity')}</th>
                <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('competition')}>Competition {sortIcon('competition')}</th>
                <th className="p-3 text-center font-medium cursor-pointer" onClick={() => toggleSort('firstPageAvgPrice')}>Avg Price {sortIcon('firstPageAvgPrice')}</th>
                <th className="p-3 font-medium cursor-pointer" onClick={() => toggleSort('niche')}>Niche {sortIcon('niche')}</th>
                <th className="p-3 font-medium cursor-pointer" onClick={() => toggleSort('lastSearchedAt')}>Last Searched {sortIcon('lastSearchedAt')}</th>
                <th className="p-3 text-center font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedHistory.map(row => {
                const isOpen = expanded.has(row.id);
                const comp = Number(row.competition ?? row.totalProducts ?? 0);
                const pop = (typeof row.popularity === 'number') ? row.popularity : null;

                return (
                  <React.Fragment key={row.id}>
                    <tr className="border-b border-slate-100 hover:bg-sky-50/50 transition-colors">
                      <td className="p-3 align-middle">
                        <button
                          onClick={() => toggleExpand(row.id)}
                          className="text-slate-500 hover:text-slate-800"
                          title={isOpen ? 'Collapse related' : 'Expand related'}
                        >
                          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      </td>
                      <td className="p-3 font-medium text-slate-700 align-middle break-words">
                        <button onClick={() => handleSearch(row.keyword, 'history_keyword_link')} className="text-sky-600 hover:underline">
                          {row.keyword}
                        </button>
                      </td>
                      <td className="p-3 text-center align-middle font-semibold text-slate-700">
                        {pop === null ? '—' : Number(pop).toLocaleString()}
                      </td>
                      <td className="p-3 text-center align-middle font-semibold text-slate-600">
                        {comp.toLocaleString()}
                      </td>
                      <td className="p-3 text-center align-middle font-semibold text-green-700">
                        ${Number(row.firstPageAvgPrice || 0).toFixed(2)}
                      </td>
                      <td className="p-3 align-middle w-1/5">
                        <Select
                          options={NICHE_CATEGORIES}
                          value={NICHE_CATEGORIES.find(c => c.value === row.niche)}
                          onChange={(selected) => handleUpdateNiche(row.id, selected.value)}
                          styles={{
                            control: (p) => ({ ...p, minHeight: 30, height: 30, fontSize: '0.75rem', width: '100%' }),
                            indicatorsContainer: (p) => ({ ...p, height: 30 }),
                            valueContainer: (p) => ({ ...p, padding: '0 8px' }),
                            singleValue: (p) => ({ ...p, fontSize: '0.85rem' }),
                            option: (p) => ({ ...p, fontSize: '0.85rem' }),
                          }}
                        />
                      </td>
                      <td className="p-3 text-slate-500 align-middle">
                        {formatLastSearched(row.lastSearchedAt)}
                      </td>
                      <td className="p-3 text-center align-middle">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => handleSearch(row.keyword, 'history_analyze_button')} className="p-1.5 text-slate-500 hover:text-sky-600" title="Run Analysis">
                            <SearchIcon size={14} />
                          </button>
                          <button onClick={() => fetchPopularityHistory(row.keyword)} className="p-1.5 text-slate-500 hover:text-purple-600" title="View Trend Analysis">
                            <BarChart2 size={14} />
                          </button>
                          <button onClick={() => openDeleteConfirm(row.id)} className="p-1.5 text-slate-500 hover:text-red-600" title="Delete Keyword History">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="bg-slate-50/50">
                        <td colSpan={8} className="p-3">
                          {Array.isArray(row.related) && row.related.length > 0 ? (
                            <div className="overflow-x-auto">
                              <p className="text-[11px] text-slate-400 italic mb-2">
                                ★ = last known data from a previous search, however old. TPT no longer publishes live popularity data for suggested keywords — Popularity shows only when we have a prior recorded value. This table only ever shows what's already stored; click "Check" or "Refresh" on any row to pull a live competition count.
                              </p>
                              <table className="w-full text-left text-xs">
                                <thead className="text-slate-500 uppercase">
                                  <tr>
                                    <th className="p-2">Related Keyword</th>
                                    <th className="p-2 text-center">Popularity</th>
                                    <th className="p-2 text-center">Competition</th>
                                    <th className="p-2 text-center">Tier</th>
                                    <th className="p-2 text-center">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {row.related.map((rel, idx) => {
                                    const check = relatedCompetitionChecks[rel.keyword] || { status: 'idle' };

                                    // Older history records (written before this field was added) store real
                                    // historical data under the original 'popularity'/'competition' names —
                                    // fall back to those whenever the newer cached* fields aren't present.
                                    const effectivePopularity = rel?.cachedPopularity ?? (typeof rel?.popularity === 'number' ? rel.popularity : null);
                                    const effectiveCompetition = rel?.cachedCompetition ?? (typeof rel?.competition === 'number' ? rel.competition : null);
                                    const hasCachedData = effectiveCompetition != null || effectivePopularity != null;

                                    const comp = check.status === 'done'
                                      ? check.competition
                                      : (hasCachedData ? effectiveCompetition : null);
                                    // Only real historical popularity counts as a demand signal here — our own
                                    // searchCount is too sparse a metric yet to grade against these thresholds.
                                    const demandCount = effectivePopularity;
                                    const hasDemandData = demandCount != null && demandCount > 0;

                                    const badge = computeOpportunityBadge({
                                      isAiSuggested: rel.source === 'ai_suggested',
                                      competition: comp,
                                      demandCount,
                                      hasDemandData,
                                    });

                                    const cachedDateLabel = rel?.cachedAt
                                      ? new Date(rel.cachedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                      : null;

                                    return (
                                      <tr key={`${row.id}-rel-${idx}`} className="border-t border-slate-200">
                                        <td className="p-2">
                                          <button onClick={() => handleSearch(rel.keyword, 'history_related_keyword_link')} className="text-sky-600 hover:underline">
                                            {rel.keyword}
                                          </button>
                                        </td>
                                        <td className="p-2 text-center">
                                          {effectivePopularity != null ? (
                                            <span className="inline-flex items-center gap-1">
                                              <span className="text-amber-500" title={cachedDateLabel ? `Cached from ${cachedDateLabel}` : 'From a previous search'}>★</span>
                                              {Number(effectivePopularity).toLocaleString()}
                                            </span>
                                          ) : '—'}
                                        </td>
                                        <td className="p-2 text-center">
                                          {comp != null ? (
                                            <span className="inline-flex items-center gap-1">
                                              {Number(comp).toLocaleString()}
                                              <button onClick={() => handleCheckRelatedCompetition(rel.keyword)} className="text-sky-600 hover:underline text-[10px]">(Refresh)</button>
                                            </span>
                                          ) : check.status === 'loading' ? (
                                            <span className="inline-flex items-center gap-1 text-slate-400">
                                              <Loader2 className="w-3 h-3 animate-spin" /> Checking...
                                            </span>
                                          ) : check.status === 'error' ? (
                                            <button onClick={() => handleCheckRelatedCompetition(rel.keyword)} className="text-red-500 hover:underline">Retry</button>
                                          ) : (
                                            <button onClick={() => handleCheckRelatedCompetition(rel.keyword)} className="text-sky-600 hover:underline">Check</button>
                                          )}
                                        </td>
                                        <td className="p-2 text-center">
                                          {badge ? (
                                            <span className={badge.className}>{badge.icon} {badge.text}</span>
                                          ) : '—'}
                                        </td>
                                        <td className="p-2 text-center">
                                          <div className="flex items-center justify-center gap-1">
                                            <button onClick={() => handleSearch(rel.keyword, 'history_related_analyze_button')} className="px-2 py-1 text-xs rounded bg-sky-600 text-white hover:bg-sky-700">Analyze</button>
                                            <button onClick={() => fetchPopularityHistory(rel.keyword)} className="p-1 text-slate-400 hover:text-purple-600" title="View Trend Analysis">
                                              <BarChart2 size={13} />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-slate-500 text-sm">No related keywords saved for this query yet.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {sortedHistory.length === 0 && historyActiveSearch && (
                  <tr>
                      <td colSpan={8} className="text-center p-6 text-slate-500">
                          No saved keywords match "{historyActiveSearch}".
                          <button onClick={clearHistorySearch} className="ml-2 text-sky-600 hover:underline">Clear Search</button>
                      </td>
                  </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* LOAD MORE BUTTON */}
        {userHistory.length >= historyLimit && (
            <div className="flex justify-center mt-4">
                <button
                    onClick={() => setHistoryLimit(prev => prev + 15)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-sky-600 bg-sky-50 rounded-full hover:bg-sky-100 transition-colors"
                >
                    <ArrowDownCircle size={16} /> Load More History
                </button>
            </div>
        )}
      </>
    );
  }, [
    currentUser, isHistoryLoading, historyLoaded, userHistory, sortedHistory,
    nicheFilter, expanded, handleSearch, handleUpdateNiche, handleExportCSV,
    openDeleteConfirm, historyLimit, historySearchInput, historyActiveSearch,
    handleHistorySearch, sortIcon
  ]);

  return (
    <>
      <div className="space-y-8 font-sans">
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={`Are you sure you want to delete the research history for the keyword "${keywordToDelete}"? This action cannot be undone.`}
          onConfirm={handleDeleteKeyword}
          onCancel={() => setShowConfirmModal(false)}
        />

        <section className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg border border-slate-200">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-sky-700 flex items-center gap-2">
                <Layers className="h-6 w-6 text-sky-600" /> Keyword Research Tool
              </h2>
              <p className="text-sm text-slate-500 mt-1">Research TPT keywords, track trends over time, and discover low-competition opportunities to grow your store.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => { setShowInteractiveModal(true); if (analytics) logEvent(analytics, 'guide_click', { content_type: 'interactive_walkthrough', item_id: 'keyword_research_tool_interactive' }); }}
                className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-3 py-2 rounded-md transition-colors"
              >
                <MousePointerClick size={14} /> Interactive Walkthrough
              </button>
              <button
                type="button"
                onClick={() => { setShowGuideModal(true); if (analytics) logEvent(analytics, 'guide_click', { content_type: 'how_to_guide', item_id: 'keyword_research_tool_guide' }); }}
                className="flex items-center gap-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold px-3 py-2 rounded-md transition-colors"
              >
                <Video size={14} /> View Guide
              </button>
            </div>
          </div>

          {isTrial && (
            <div className="mb-0 mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-700">Trial Usage:</span>
                <span className="text-sm text-slate-600">
                  You have used <span className={isLimitReached ? "text-red-600 font-bold" : "text-sky-600 font-bold"}>{usageCount}</span> of <span className="font-bold">{TRIAL_LIMIT}</span> searches.
                </span>
              </div>
              {isLimitReached && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-medium flex items-center gap-1">
                  <Lock size={12} /> Limit Reached
                </span>
              )}
            </div>
          )}

          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block mt-4">
            📊 <strong>Popularity</strong> is currently untracked — TPT removed this data publicly. Previously searched popularity scores remain visible where available.
          </p>
        </section>

        {/* ===== TABBED PANEL ===== */}
        <section className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">

          {/* Tab Headers */}
          <div className="flex border-b border-slate-200">
            {[
              { id: 'search', label: 'Search', icon: <SearchIcon size={15} /> },
              { id: 'history', label: 'My History', icon: <Clock size={15} /> },
              { id: 'opportunities', label: 'Top Opportunities', icon: <TrendingUp size={15} /> },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-sky-600 text-sky-700 bg-sky-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* ---- SEARCH TAB ---- */}
          {activeTab === 'search' && (
            <div className="p-6 space-y-6">
              <p className="text-sm text-slate-500">
                Search any TPT keyword to view competition, average pricing, and related opportunities. Data is globally cached for 7 days.
              </p>
              <div className="flex gap-4">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !isLimitReached && handleSearch(searchTerm, 'main_search_bar')}
                  placeholder={isLimitReached ? "Trial limit reached - please upgrade" : "Enter a TPT keyword, e.g. 'solar system mini book'"}
                  className="flex-1 p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                  disabled={isSearching || isLimitReached}
                />
                <button
                  onClick={() => handleSearch(searchTerm, 'main_search_bar')}
                  disabled={isSearching || !searchTerm.trim() || isLimitReached}
                  className="w-40 flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-lg shadow-md transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed"
                >
                  {isSearching ? <Loader2 className="animate-spin" /> : <SearchIcon size={18} />}
                  <span>{isSearching ? 'Searching...' : 'Analyze'}</span>
                </button>
              </div>
              {isLimitReached && (
                <div className="text-sm text-red-700 bg-red-50 p-3 rounded-lg border border-red-100 flex items-start gap-2">
                  <Zap size={18} className="mt-0.5 flex-shrink-0" />
                  <span><strong>You've hit the trial limit!</strong> Upgrade to Full Access for unlimited keyword research.</span>
                </div>
              )}
              {error && <p className="text-red-600 bg-red-50 p-3 rounded-lg text-sm">{error}</p>}
              {tabAnalysisResult.search && (
                <div className="animate-fade-in">{SearchResultDisplay}</div>
              )}
            </div>
          )}

          {/* ---- HISTORY TAB ---- */}
          {activeTab === 'history' && (
            <div className="p-6 space-y-6">
              {tabAnalysisResult.history && (
                <div className="animate-fade-in">{SearchResultDisplay}</div>
              )}
              <div>
                <p className="text-sm text-slate-500 mb-4">
                  Keywords you've searched are saved here with related suggestions. Click the arrow to expand a keyword and see its opportunity bucket.
                </p>
                {HistoryTable}
              </div>
            </div>
          )}

          {/* ---- OPPORTUNITIES TAB ---- */}
          {activeTab === 'opportunities' && (
            <div className="p-6 space-y-6">
              {tabAnalysisResult.opportunities && (
                <div className="animate-fade-in">{SearchResultDisplay}</div>
              )}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">
                      Keywords with the best opportunities from your database — analyzed from the last 30 days. Updated every 2 days.
                    </p>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">
                      💡 <strong>Golden Score</strong> = High teacher search demand vs low product competition. The higher the score, the bigger the opportunity gap.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {trendingRefreshedAt?.seconds && (
                      <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2 py-1 rounded-full whitespace-nowrap">
                        🕐 Refreshed {new Date(trendingRefreshedAt.seconds * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET
                      </span>
                    )}
                    {currentOpportunities.length > 0 && (
                      <button
                        onClick={downloadOpportunitiesCSV}
                        className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
                        title="Download as CSV"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        CSV
                      </button>
                    )}
                  </div>
                </div>

                {/* Opportunity View Tabs */}
                <div className="mb-4">
                  <nav className="flex space-x-1 bg-slate-100 rounded-lg p-1">
                    {[
                      { id: 'topOpportunities', label: '🏆 Top Opportunities', desc: 'Best Golden Score' },
                      { id: 'mostPopular', label: '🔥 Most Popular', desc: 'Highest search volume' },
                      { id: 'lowCompetition', label: '💎 Low Competition', desc: 'Easiest to rank' }
                    ].map(view => (
                      <button
                        key={view.id}
                        onClick={() => setActiveOpportunityView(view.id)}
                        className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                          activeOpportunityView === view.id
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                        title={view.desc}
                      >
                        {view.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {!currentUser ? (
                  <p className="text-center p-4 text-sm text-slate-500">Log in to see top opportunities.</p>
                ) : isTrendingLoading ? (
                  <div className="flex justify-center p-8"><Loader2 className="animate-spin text-sky-500" /></div>
                ) : trendingKeywords.length === 0 ? (
                  <p className="text-center p-4 text-sm text-slate-400">No data yet — check back soon.</p>
                ) : (
                  <div className="overflow-x-auto mt-4">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                          <th className="p-3 w-8">#</th>
                          <th className="p-3">Keyword</th>
                          <th className="p-3 text-center">Golden Score</th>
                          <th className="p-3 text-center">Popularity</th>
                          <th className="p-3 text-center">Competition</th>
                          <th className="p-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trendingKeywords.map((item, idx) => (
                          <tr key={item.keyword} className="border-b border-slate-100 hover:bg-sky-50/50 transition-colors">
                            <td className="p-3 text-slate-400 font-semibold text-xs">{idx + 1}</td>
                            <td className="p-3 font-medium text-slate-700">
                              <button onClick={() => handleSearch(item.keyword, 'trending_analyze')} className="text-sky-600 hover:underline text-left">
                                {item.keyword}
                              </button>
                            </td>
                            <td className="p-3 text-center">
                              <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full border ${
                                item.goldenScore >= 60 ? 'bg-green-50 text-green-700 border-green-200' :
                                item.goldenScore >= 30 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-slate-50 text-slate-600 border-slate-200'
                              }`}>
                                ✦ {item.goldenScore ?? '—'}
                              </span>
                            </td>
                            <td className="p-3 text-center font-semibold text-sky-700">
                              {item.popularity != null ? item.popularity.toLocaleString() : '—'}
                            </td>
                            <td className="p-3 text-center font-semibold text-slate-600">
                              {item.totalProducts != null ? item.totalProducts.toLocaleString() : '—'}
                            </td>
                            <td className="p-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => handleSearch(item.keyword, 'trending_analyze')} className="px-2 py-1 text-xs rounded bg-sky-600 text-white hover:bg-sky-700">
                                  Analyze
                                </button>
                                <button onClick={() => fetchPopularityHistory(item.keyword)} className="px-2 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300">
                                  Trend
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

        </section>

      </div>

      {/* Guide Modal */}
      {showGuideModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowGuideModal(false)}
        >
          <div
            className="w-full max-w-5xl h-[90vh] rounded-xl bg-slate-800 border border-slate-700 shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Keyword Research Tool Guide
              </h3>
              <button
                type="button"
                onClick={() => setShowGuideModal(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 p-0 md:p-4 overflow-hidden">
              <iframe
                src="https://scribehow.com/embed/How_to_use_the_Keyword_Research_Tool__SN7UmJi2TZWYxt_hOhwuwg?as=scrollable"
                width="100%"
                height="100%"
                allow="fullscreen"
                style={{ border: 0, minHeight: '640px' }}
                title="Keyword Research Tool Guide"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Interactive Walkthrough Modal */}
      {showInteractiveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowInteractiveModal(false)}
        >
          <div
            className="w-full max-w-7xl rounded-xl bg-slate-800 border border-slate-700 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-sky-400">
                Interactive Walkthrough
              </h3>
              <button
                type="button"
                onClick={() => setShowInteractiveModal(false)}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>
            <div
                style={{
                    position: 'relative',
                    boxSizing: 'content-box',
                    maxHeight: '80vh',
                    width: '100%',
                    aspectRatio: '2.013986013986014',
                    padding: '40px 0 40px 0',
                    background: '#1e293b'
                }}
            >
                <iframe
                    src="https://app.supademo.com/embed/cmi4u11011vekb7b4t07c5480?embed_v=2&utm_source=embed"
                    loading="lazy"
                    title="Mr Joel's Seller Tool - TPT Keyword Research"
                    allow="clipboard-write"
                    frameBorder="0"
                    webkitallowfullscreen="true"
                    mozallowfullscreen="true"
                    allowFullScreen
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }}
                ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* ===== TREND HISTORY MODAL ===== */}
      {showTrendModal && (
        <TrendModal
          keyword={activeHistoryKeyword}
          history={popularityHistory}
          isLoading={isHistoryChartLoading}
          onClose={() => setShowTrendModal(false)}
        />
      )}
    </>
  );
};

export default KeywordResearchTool;